import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getCalendarTasks } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Building
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, isToday, getDay, isSameMonth } from 'date-fns';

import { useDepartments } from '../hooks/useDepartments';

const getStatusDot = (status) => {
  switch (status) {
    case 'Completed': case 'Done': return 'bg-emerald-500';
    case 'In Progress': return 'bg-blue-500';
    case 'In Review': return 'bg-amber-500';
    default: return 'bg-slate-400';
  }
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const CalendarPage = () => {
  const DEPARTMENTS = useDepartments();
  const { user, isSuperAdmin } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [departmentFilter, setDepartmentFilter] = useState('');

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);
      const params = {
        start_date: monthStart.toISOString(),
        end_date: monthEnd.toISOString(),
      };
      if (departmentFilter) params.department = departmentFilter;
      const response = await getCalendarTasks(params);
      setCalendarData(response.data || {});
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      setCalendarData({});
    } finally {
      setLoading(false);
    }
  }, [currentMonth, departmentFilter]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOffset = getDay(monthStart);

  const getTasksForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendarData[dateStr] || [];
  };

  const selectedDateTasks = selectedDate ? getTasksForDate(selectedDate) : [];

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Calendar</h1>
          <p className="text-muted-foreground mt-1">View tasks by due date</p>
        </div>
        <div className="flex items-center gap-3">
          {isSuperAdmin && (
            <Select value={departmentFilter || "all"} onValueChange={(v) => setDepartmentFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-56" data-testid="department-filter">
                <Building className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {DEPARTMENTS.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-3">
          <Card className="border-border/50">
            <CardContent className="p-4 sm:p-6">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-6">
                <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} data-testid="prev-month">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <h2 className="text-xl font-semibold" data-testid="current-month">
                  {format(currentMonth, 'MMMM yyyy')}
                </h2>
                <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} data-testid="next-month">
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
                </div>
              ) : (
                <>
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 mb-2">
                    {DAY_NAMES.map(day => (
                      <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
                    {/* Empty cells for offset */}
                    {Array.from({ length: startDayOffset }).map((_, i) => (
                      <div key={`empty-${i}`} className="bg-background/50 min-h-[80px] sm:min-h-[100px] p-1" />
                    ))}
                    {days.map((day) => {
                      const dayTasks = getTasksForDate(day);
                      const isSelected = selectedDate && isSameDay(day, selectedDate);
                      const today = isToday(day);

                      return (
                        <div
                          key={day.toISOString()}
                          className={`bg-background min-h-[80px] sm:min-h-[100px] p-1.5 cursor-pointer transition-colors hover:bg-accent/50 ${
                            isSelected ? 'ring-2 ring-[#F5A623] ring-inset' : ''
                          }`}
                          onClick={() => setSelectedDate(day)}
                          data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                        >
                          <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                            today ? 'bg-[#F5A623] text-black' : ''
                          }`}>
                            {format(day, 'd')}
                          </div>
                          <div className="space-y-0.5">
                            {dayTasks.slice(0, 3).map((task) => (
                              <div key={task.id} className="flex items-center gap-1 group">
                                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${getStatusDot(task.status)}`} />
                                <span className="text-[10px] truncate leading-tight">{task.task_name}</span>
                              </div>
                            ))}
                            {dayTasks.length > 3 && (
                              <span className="text-[10px] text-muted-foreground font-medium">+{dayTasks.length - 3} more</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Selected Date Panel */}
        <div>
          <Card className="border-border/50 sticky top-20">
            <CardContent className="p-4">
              {selectedDate ? (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <CalendarIcon className="h-4 w-4 text-[#F5A623]" />
                    <h3 className="font-semibold" data-testid="selected-date-title">
                      {format(selectedDate, 'EEEE, MMM d')}
                    </h3>
                  </div>
                  {selectedDateTasks.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No tasks due on this date</p>
                  ) : (
                    <div className="space-y-3">
                      {selectedDateTasks.map((task) => (
                        <a
                          key={task.id}
                          href={`/tasks/${task.id}`}
                          className="block p-3 rounded-lg border border-border hover:border-[#F5A623]/50 transition-colors"
                          data-testid={`calendar-task-${task.id}`}
                        >
                          <div className="flex items-start gap-2 mb-2">
                            <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${getStatusDot(task.status)}`} />
                            <p className="text-sm font-medium leading-tight">{task.task_name}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{task.status}</Badge>
                            {task.assigned_to_name && (
                              <span className="text-[10px] text-muted-foreground">{task.assigned_to_name}</span>
                            )}
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <CalendarIcon className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Click a date to view tasks</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="font-medium">Status:</span>
        {[
          { status: 'To Do', color: 'bg-slate-400' },
          { status: 'In Progress', color: 'bg-blue-500' },
          { status: 'In Review', color: 'bg-amber-500' },
          { status: 'Completed', color: 'bg-emerald-500' },
        ].map(({ status, color }) => (
          <div key={status} className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${color}`} />
            <span>{status}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
